import { useEffect, useState, useCallback } from "react";
import { API_HOST } from "../constants";
import { useUser } from "../contexts/UserContext";
import type { TelegramUser, Progress, ProgressItem } from "../types";

const STORAGE_PREFIX = "progress_";

export const useUserProgress = (user: TelegramUser | null) => {
  const { user: contextUser, refreshUser } = useUser();
  // Используем пользователя из контекста, если он доступен, иначе из props
  const activeUser = contextUser || user;
  const [progress, setProgress] = useState<Progress>({});
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState<number>(0);

  const fetchBackendProgress = useCallback(async () => {
    if (!activeUser) return null;

    try {
      // Используем данные из контекста, если доступны
      if (contextUser?.progress) {
        return contextUser.progress as Progress;
      }
      
      // Если данных нет в контексте, обновляем их
      await refreshUser();
      // После refreshUser contextUser может обновиться, но мы не можем его использовать напрямую
      // Возвращаем пустой объект, чтобы избежать зависимости от contextUser
      return {} as Progress;
    } catch (error) {
      console.error("Error fetching progress from server:", error);
      return null;
    }
    // Убрали contextUser и refreshUser из зависимостей - используем только activeUser
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser]);

  useEffect(() => {
    if (!activeUser) {
      setProgress({});
      return;
    }

    const loadProgress = async () => {
      setLoading(true);
      const key = STORAGE_PREFIX + activeUser.id;

      // Сначала используем данные из localStorage (быстрое отображение)
      const savedStr = localStorage.getItem(key);
      const localProgress: Progress = savedStr ? JSON.parse(savedStr) : {};

      // Используем прогресс из контекста (если есть, уже загружен при авторизации)
      // Конвертируем строковые ключи в числовые, если нужно
      let userProgress: Progress = {};
      const userData = contextUser || activeUser;
      if (userData.progress) {
        Object.entries(userData.progress).forEach(([k, v]) => {
          const taskId = parseInt(k, 10);
          if (!isNaN(taskId)) {
            userProgress[taskId] = v as ProgressItem;
          }
        });
      }

      // Объединяем: user.progress имеет приоритет (самые свежие данные с сервера)
      let mergedProgress: Progress = { ...localProgress, ...userProgress };
      
      // Показываем данные сразу (не блокируем UI)
      setProgress(mergedProgress);
      setLastSync(Date.now());
      
      // Обновляем данные из контекста в фоне
      fetchBackendProgress().then((backendProgress) => {
        if (backendProgress) {
          const convertedBackend: Progress = {};
          Object.entries(backendProgress).forEach(([k, v]) => {
            const taskId = parseInt(k, 10);
            if (!isNaN(taskId)) {
              convertedBackend[taskId] = v as ProgressItem;
            }
          });
          // Объединяем с текущим прогрессом (backend имеет приоритет)
          const finalProgress = { ...mergedProgress, ...convertedBackend };
          setProgress(finalProgress);
          localStorage.setItem(key, JSON.stringify(finalProgress));
          setLastSync(Date.now());
        }
      }).catch(() => {
        // Игнорируем ошибки фоновой загрузки
      });
      
      setLoading(false);
    };

    loadProgress();
    // Убрали fetchBackendProgress из зависимостей - он стабилен теперь
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser, contextUser]);

  const updateProgress = useCallback(
    async (taskId: number, status: "success" | "error" | "idle", code?: string, error_msg?: string) => {
      if (!activeUser) return false;

      const newProgress: Progress = {
        ...progress,
        [taskId]: {
          status,
          difficulty: progress[taskId]?.difficulty || "easy",
          code,
          error_msg: status === "error" ? error_msg : status === "success" ? undefined : progress[taskId]?.error_msg
        }
      };

      setProgress(newProgress);
      localStorage.setItem(STORAGE_PREFIX + activeUser.id, JSON.stringify(newProgress));

      try {
        await fetch(`${API_HOST}/user/${activeUser.id}/progress`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ task_id: taskId, status, code, error_msg })
        });
        
        // Обновляем контекст пользователя после успешного сохранения
        if (contextUser) {
          refreshUser();
        }
      } catch (error) {
        console.error("Failed to sync progress with server", error);
      }

      return true;
    },
    // Убрали refreshUser из зависимостей - он стабилен теперь
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeUser, contextUser, progress]
  );

  const syncProgress = useCallback(async (force = false) => {
    if (!activeUser) return false;

    // Принудительно обновляем данные из контекста
    if (force) {
      await refreshUser();
    }

    const backendProgress = await fetchBackendProgress();
    if (backendProgress) {
      const key = STORAGE_PREFIX + activeUser.id;
      const convertedBackend: Progress = {};
      Object.entries(backendProgress).forEach(([k, v]) => {
        const taskId = parseInt(k, 10);
        if (!isNaN(taskId)) convertedBackend[taskId] = v as ProgressItem;
      });

      // Используем функциональное обновление состояния, чтобы не зависеть от текущего progress
      setProgress(prevProgress => {
        const mergedProgress = { ...prevProgress, ...convertedBackend };
        localStorage.setItem(key, JSON.stringify(mergedProgress));
        setLastSync(Date.now());
        return mergedProgress;
      });
      return true;
    }

    return false;
    // Убрали fetchBackendProgress и refreshUser из зависимостей - они стабильны теперь
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser]); // Убрали progress из зависимостей

  return {
    progress,
    updateProgress,
    loading,
    lastSync,
    syncProgress
  };
};
